import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Package, Pencil, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { fmt } from '@/lib/format';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/cn';
import { ArticuloFormModal } from './ArticuloFormModal';

type Articulo = {
  id: string;
  clave: string;
  descripcion: string;
  categoria_id?: string;
  categoria_nombre?: string;
  unidad_medida_id?: string;
  unidad_clave?: string;
  clave_sat?: string;
  marca?: string;
  modelo?: string;
  presentacion?: string;
  codigo_barras?: string;
  partida_cog_id?: string;
  partida_cog_codigo?: string;
  cuenta_contable_id?: string;
  costo_promedio: number;
  minimo: number;
  maximo: number;
  punto_reorden: number;
  controla_lote: boolean;
  controla_serie: boolean;
  notas?: string;
};

type Existencia = {
  almacen_id: string;
  almacen_clave: string;
  almacen_nombre: string;
  cantidad: number;
  costo_promedio: number;
  valor_total: number;
  bajo_minimo: boolean;
  en_reorden: boolean;
};

type Kardex = {
  fecha: string;
  folio: string;
  tipo: string;
  lote?: string;
  cantidad_signo: number;
  costo_unitario: number;
  total: number;
  saldo_acumulado: number;
  concepto?: string;
};

export function ArticuloDetallePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [borrando, setBorrando] = useState(false);

  const { data: a, refetch } = useQuery({
    queryKey: ['articulo', id],
    queryFn: () => api<Articulo>(`/articulos/${id}`),
  });

  async function eliminar() {
    if (!confirm(`¿Dar de baja el artículo ${a?.clave}? Queda inactivo pero conserva historial.`)) return;
    setBorrando(true);
    try {
      await api(`/articulos/${id}`, { method: 'DELETE' });
      navigate('/app/articulos');
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'No se pudo eliminar');
      setBorrando(false);
    }
  }
  const { data: existencias } = useQuery({
    queryKey: ['existencias-articulo', id],
    queryFn: () => api<Existencia[]>(`/existencias?articulo_id=${id}&solo_ceros=true`),
  });
  const { data: kardex } = useQuery({
    queryKey: ['kardex', id],
    queryFn: () => api<Kardex[]>(`/articulos/${id}/kardex`),
  });

  if (!a) return <div className="p-10 text-[13px] text-ink-mute">Cargando…</div>;

  const totalExistencia = (existencias ?? []).reduce((acc, e) => acc + e.cantidad, 0);
  const valorTotal = (existencias ?? []).reduce((acc, e) => acc + e.valor_total, 0);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/app/articulos" className="inline-flex items-center gap-1.5 text-[12px] font-medium text-ink-mute hover:text-ink">
          <ArrowLeft size={14} /> Volver a artículos
        </Link>
        <div className="mt-3 flex items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-900/40">
              <Package size={20} />
            </div>
            <div>
              <p className="num text-[12px] font-semibold uppercase tracking-[0.14em] text-brand-700 dark:text-brand-300">{a.clave}</p>
              <h1 className="font-display text-[24px] font-bold tracking-tight text-ink">{a.descripcion}</h1>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setEditOpen(true)}>
              <Pencil size={14} /> Editar
            </Button>
            <Button variant="ghost" onClick={eliminar} loading={borrando} className="text-danger hover:bg-rose-50 dark:hover:bg-rose-900/20">
              <Trash2 size={14} /> Dar de baja
            </Button>
          </div>
        </div>
      </div>

      <ArticuloFormModal
        open={editOpen}
        id={id}
        initial={{
          clave: a.clave,
          descripcion: a.descripcion,
          unidad_medida_id: a.unidad_medida_id,
          categoria_id: a.categoria_id,
          partida_cog_id: a.partida_cog_id,
          cuenta_contable_id: a.cuenta_contable_id,
          clave_sat: a.clave_sat,
          marca: a.marca,
          modelo: a.modelo,
          presentacion: a.presentacion,
          codigo_barras: a.codigo_barras,
          minimo: a.minimo,
          maximo: a.maximo,
          punto_reorden: a.punto_reorden,
          controla_lote: a.controla_lote,
          controla_serie: a.controla_serie,
          notas: a.notas,
        }}
        onClose={() => setEditOpen(false)}
        onCreated={() => {
          setEditOpen(false);
          qc.invalidateQueries({ queryKey: ['articulo', id] });
          qc.invalidateQueries({ queryKey: ['articulos'] });
          refetch();
        }}
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <Stat label="Existencia total" value={`${fmt.num(totalExistencia)} ${a.unidad_clave ?? ''}`} />
        <Stat label="Valor en almacenes" value={fmt.money(valorTotal)} />
        <Stat label="Costo promedio" value={fmt.money(a.costo_promedio)} />
        <Stat label="Stock mínimo" value={fmt.num(a.minimo)} />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card>
          <CardHeader title="Información" />
          <CardBody className="space-y-2 text-[13px]">
            <Row label="Categoría" value={a.categoria_nombre ?? '—'} />
            <Row label="Unidad" value={a.unidad_clave ?? '—'} />
            <Row label="Clave SAT" value={a.clave_sat ?? '—'} />
            <Row label="Partida COG" value={a.partida_cog_codigo ?? '—'} />
            <Row label="Marca" value={a.marca ?? '—'} />
            <Row label="Modelo" value={a.modelo ?? '—'} />
            <Row label="Presentación" value={a.presentacion ?? '—'} />
            <Row label="Código barras" value={a.codigo_barras ?? '—'} />
            <Row label="Controla lote" value={a.controla_lote ? 'Sí' : 'No'} />
            <Row label="Controla serie" value={a.controla_serie ? 'Sí' : 'No'} />
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Existencias por almacén" />
          <CardBody className="p-0">
            {!existencias || existencias.length === 0 ? (
              <div className="p-6 text-center text-[12px] text-ink-mute">Sin existencias registradas.</div>
            ) : (
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                  <tr>
                    <th className="px-4 py-2">Almacén</th>
                    <th className="px-4 py-2 text-right">Cantidad</th>
                    <th className="px-4 py-2 text-right">Valor</th>
                    <th className="px-4 py-2">Estado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {existencias.map((e) => (
                    <tr key={e.almacen_id}>
                      <td className="px-4 py-2">{e.almacen_clave}</td>
                      <td className="num px-4 py-2 text-right">{fmt.num(e.cantidad)}</td>
                      <td className="num px-4 py-2 text-right">{fmt.money(e.valor_total)}</td>
                      <td className="px-4 py-2">
                        {e.bajo_minimo ? (
                          <span className="rounded bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-rose-700 dark:bg-rose-900/40 dark:text-rose-200">Bajo mín.</span>
                        ) : e.en_reorden ? (
                          <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">Reorden</span>
                        ) : (
                          <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">OK</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader title="Kardex" subtitle="Movimientos cronológicos del artículo con saldo acumulado" />
        <CardBody className="p-0">
          {!kardex || kardex.length === 0 ? (
            <div className="p-10 text-center text-[13px] text-ink-mute">Sin movimientos.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-[13px]">
                <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-mute">
                  <tr>
                    <th className="px-5 py-3">Fecha</th>
                    <th className="px-5 py-3">Folio</th>
                    <th className="px-5 py-3">Tipo</th>
                    <th className="px-5 py-3">Concepto</th>
                    <th className="px-5 py-3 text-right">Movimiento</th>
                    <th className="px-5 py-3 text-right">Costo</th>
                    <th className="px-5 py-3 text-right">Total</th>
                    <th className="px-5 py-3 text-right">Saldo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {kardex.map((k, i) => (
                    <tr key={i}>
                      <td className="num px-5 py-3">{fmt.date(k.fecha)}</td>
                      <td className="num px-5 py-3 font-semibold">{k.folio}</td>
                      <td className="px-5 py-3">
                        <span className={cn(
                          'rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase',
                          k.tipo.startsWith('entrada')
                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200'
                            : 'bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200'
                        )}>
                          {k.tipo.replace('entrada_', '↑').replace('salida_', '↓').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-5 py-3 max-w-[260px] truncate text-ink-mute">{k.concepto ?? '—'}</td>
                      <td className={cn(
                        'num px-5 py-3 text-right font-semibold',
                        k.cantidad_signo > 0 ? 'text-emerald-700' : 'text-rose-700'
                      )}>
                        {k.cantidad_signo > 0 ? '+' : ''}{fmt.num(k.cantidad_signo)}
                      </td>
                      <td className="num px-5 py-3 text-right">{fmt.money(k.costo_unitario)}</td>
                      <td className="num px-5 py-3 text-right">{fmt.money(k.total)}</td>
                      <td className="num px-5 py-3 text-right font-semibold">{fmt.num(k.saldo_acumulado)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-border bg-surface p-5 shadow-card">
      <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-mute">{label}</span>
      <div className="num mt-2 text-[22px] font-semibold text-ink">{value}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 border-b border-border/60 pb-1.5 last:border-0">
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-soft">{label}</span>
      <span className="text-ink">{value}</span>
    </div>
  );
}
